<template>
  <div class="conversation-diff" data-testid="conversation-diff">
    <div v-if="!diff || !diff.files || diff.files.length === 0" class="conversation-diff-empty" data-testid="conversation-diff-empty">
      <span v-if="diff && diff.reason === 'no_draft_commit'">No diff is available for this turn (no draft commit was recorded).</span>
      <span v-else-if="diff && diff.reason === 'commit_not_found'">The draft commit for this turn is no longer reachable.</span>
      <span v-else-if="diff && diff.reason === 'invalid_commit_hash'">The recorded commit hash is invalid; the diff can't be loaded.</span>
      <span v-else>No file changes to show.</span>
    </div>
    <div v-else>
      <div
        v-for="file in diff.files"
        :key="file.path"
        class="conversation-diff-file"
        data-testid="conversation-diff-file"
      >
        <div class="conversation-diff-file-header">
          <span class="conversation-diff-file-status" :data-status="file.status">{{ statusLabel(file.status) }}</span>
          <span class="conversation-diff-file-path" :title="file.path">{{ file.path }}</span>
          <span class="conversation-diff-file-stats">
            <span v-if="file.insertions > 0" class="conversation-diff-ins">+{{ file.insertions }}</span>
            <span v-if="file.deletions > 0" class="conversation-diff-del">-{{ file.deletions }}</span>
          </span>
        </div>
        <!-- Truncated guard — files past the 500-line cap render a summary
             only, with a note so the user knows there's more behind it. -->
        <div
          v-if="file.truncated"
          class="conversation-diff-truncated"
          data-testid="conversation-diff-truncated"
        >
          File is too large to render inline ({{ file.insertions + file.deletions }} changed lines). Showing first 500 lines.
        </div>
        <!-- Hunks. First 2 render expanded; the rest sit behind a "Show N
             more changes" toggle so a 10-hunk file doesn't dominate the
             panel. -->
        <template v-for="(hunk, idx) in visibleHunks(file)" :key="idx">
          <div class="conversation-diff-hunk" data-testid="conversation-diff-hunk">
            <div class="conversation-diff-hunk-header">@@ line {{ hunk.newStart }} @@</div>
            <div
              v-for="(line, li) in hunk.lines"
              :key="li"
              class="conversation-diff-line"
              :class="`line-${line.kind}`"
              :data-kind="line.kind"
            >
              <span class="conversation-diff-gutter conversation-diff-gutter-old">{{ line.oldNum ?? '' }}</span>
              <span class="conversation-diff-gutter conversation-diff-gutter-new">{{ line.newNum ?? '' }}</span>
              <span class="conversation-diff-sigil">{{ sigil(line.kind) }}</span>
              <span class="conversation-diff-text">{{ line.text }}</span>
            </div>
          </div>
        </template>
        <button
          v-if="file.hunks.length > collapsedHunkLimit && !isHunkExpanded(file.path)"
          type="button"
          class="conversation-diff-show-more"
          data-testid="conversation-diff-show-more"
          @click="expandHunks(file.path)"
        >Show {{ file.hunks.length - collapsedHunkLimit }} more change{{ file.hunks.length - collapsedHunkLimit === 1 ? '' : 's' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

const COLLAPSED_HUNK_LIMIT = 2

export default {
  name: 'ConversationDiff',
  props: {
    diff: {
      type: Object,
      default: null
    }
  },
  setup() {
    // Per-file expanded set — files start with only the first N hunks
    // visible. Clicking "Show N more" flips the file into fully-expanded.
    const expandedFilePaths = ref(new Set())
    const expandHunks = (path) => {
      if (!path) return
      const next = new Set(expandedFilePaths.value)
      next.add(path)
      expandedFilePaths.value = next
    }
    const isHunkExpanded = (path) => expandedFilePaths.value.has(path)

    const visibleHunks = (file) => {
      if (!file || !Array.isArray(file.hunks)) return []
      if (expandedFilePaths.value.has(file.path)) return file.hunks
      if (file.hunks.length <= COLLAPSED_HUNK_LIMIT) return file.hunks
      return file.hunks.slice(0, COLLAPSED_HUNK_LIMIT)
    }

    const sigil = (kind) => {
      if (kind === 'add') return '+'
      if (kind === 'del') return '-'
      return ' '
    }

    const statusLabel = (status) => {
      if (status === 'added') return 'NEW'
      if (status === 'deleted') return 'DEL'
      if (status === 'renamed') return 'REN'
      return 'MOD'
    }

    return {
      collapsedHunkLimit: COLLAPSED_HUNK_LIMIT,
      expandHunks,
      isHunkExpanded,
      visibleHunks,
      sigil,
      statusLabel
    }
  }
}
</script>

<style scoped>
.conversation-diff { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", "Liberation Mono", monospace; font-size: 11px; line-height: 1.45; margin-top: 8px; }
.conversation-diff-empty { color: var(--text-muted); font-size: 11px; padding: 8px 10px; background: var(--glass-bg); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; }

.conversation-diff-file { margin-bottom: 8px; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; overflow: hidden; background: rgba(0,0,0,0.20); }
.conversation-diff-file:last-child { margin-bottom: 0; }

.conversation-diff-file-header { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: var(--glass-bg); border-bottom: 1px solid rgba(255,255,255,0.06); }
.conversation-diff-file-status { font-size: 9px; font-weight: 700; padding: 1px 6px; border-radius: 4px; color: var(--text-muted); background: rgba(255,255,255,0.06); }
.conversation-diff-file-status[data-status="added"] { color: #4ade80; background: rgba(74,222,128,0.10); }
.conversation-diff-file-status[data-status="deleted"] { color: #f87171; background: rgba(248,113,113,0.10); }
.conversation-diff-file-status[data-status="renamed"] { color: var(--accent); background: rgba(109,93,255,0.10); }
.conversation-diff-file-path { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-primary); font-size: 11px; }
.conversation-diff-file-stats { display: flex; gap: 6px; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.conversation-diff-ins { color: #4ade80; }
.conversation-diff-del { color: #f87171; }

.conversation-diff-truncated { padding: 6px 10px; font-size: 10px; color: var(--text-muted); background: rgba(245,158,11,0.06); border-bottom: 1px solid rgba(245,158,11,0.18); font-family: var(--font-family-base, system-ui); }

.conversation-diff-hunk { padding: 4px 0; }
.conversation-diff-hunk:not(:first-of-type) { border-top: 1px dashed rgba(255,255,255,0.06); }
.conversation-diff-hunk-header { padding: 2px 10px; font-size: 10px; color: var(--text-muted); background: rgba(255,255,255,0.02); }

.conversation-diff-line { display: flex; align-items: stretch; padding: 0 10px; white-space: pre; }
.conversation-diff-line.line-add { background: rgba(74,222,128,0.08); }
.conversation-diff-line.line-del { background: rgba(248,113,113,0.08); }
.conversation-diff-line.line-context { color: var(--text-secondary); }
.conversation-diff-gutter { display: inline-block; min-width: 28px; padding-right: 8px; color: var(--text-muted); font-size: 10px; text-align: right; flex-shrink: 0; user-select: none; }
.conversation-diff-sigil { display: inline-block; width: 12px; color: var(--text-muted); flex-shrink: 0; }
.conversation-diff-line.line-add .conversation-diff-sigil { color: #4ade80; }
.conversation-diff-line.line-del .conversation-diff-sigil { color: #f87171; }
.conversation-diff-text { flex: 1; min-width: 0; color: var(--text-primary); word-break: break-word; }

.conversation-diff-show-more { width: 100%; padding: 6px; font-size: 11px; color: var(--accent); background: transparent; border: none; border-top: 1px dashed rgba(255,255,255,0.08); cursor: pointer; transition: background .12s; }
.conversation-diff-show-more:hover { background: rgba(109,93,255,0.06); }
</style>
